import { FC, useCallback, useState } from 'react';

import classNames from 'classnames';
import { useRecoilValue } from 'recoil';

import { BellIcon } from 'assets/icons';
import { If } from 'components/Generics';
import { Loader } from 'components/Loader/Loader';
import OutsideClickHandler from 'components/OutsideClickHandler/OutsideClickHandler';
import {
  useCrmNotificationUnreadCount,
  useGetCrmNotifications
} from 'services/hooks/SalesforceCrmNotifications.hooks';
import { loginedUserDetails } from 'store/atoms';
import { CrmNotification } from 'types/SalesforceCrmNotifications.types';
import { formatDate, openRecordInNewTab, translate } from 'utils';

import {
  formatCrmNotificationBodyPreview,
  formatCrmNotificationMessage
} from '../utils/formatCrmNotification';
import { getCrmNotificationDetailPath } from '../utils/getCrmNotificationDetailPath';

const SalesforceNotificationsBell: FC = () => {
  const userDetails = useRecoilValue(loginedUserDetails);
  const [isOpen, setIsOpen] = useState(false);

  const { unreadCount } = useCrmNotificationUnreadCount(userDetails?.id);

  const { data, loading } = useGetCrmNotifications({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    skip: !isOpen
  });

  const notifications = data?.getCrmNotifications?.notifications ?? [];

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleNotificationClick = useCallback(
    (notification: CrmNotification) => {
      const path = getCrmNotificationDetailPath(notification);
      if (!path) return;

      setIsOpen(false);
      openRecordInNewTab(path);
    },
    []
  );

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <OutsideClickHandler onOutsideClick={handleClose}>
      <div className='relative'>
        <button
          type='button'
          aria-label={translate('salesforce.notifications.title')}
          aria-expanded={isOpen}
          className='relative flex items-center justify-center rounded-full p-2 hover:bg-LIGHT_GRAY_5'
          onClick={handleToggle}
        >
          <BellIcon className='h-6 w-6 text-DEFAULT_TEXT' />
          <If condition={unreadCount > 0}>
            <span className='absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-ERROR px-1 text-xs font-semibold text-WHITE'>
              {badgeLabel}
            </span>
          </If>
        </button>

        <If condition={isOpen}>
          <div className='absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-lg border border-LIGHT_GRAY bg-WHITE shadow-lg md:w-96'>
            <div className='border-b border-LIGHT_GRAY px-4 py-3 text-sm font-semibold text-DEFAULT_TEXT'>
              {translate('salesforce.notifications.title')}
            </div>

            <div className='max-h-[70vh] overflow-y-auto'>
              <If condition={loading && !notifications.length}>
                <div className='flex items-center justify-center py-8'>
                  <Loader className='h-8 w-8' containerClassName='' />
                </div>
              </If>

              <If condition={!loading && !notifications.length}>
                <p className='px-4 py-6 text-center text-sm text-DARK_GRAY'>
                  {translate('salesforce.notifications.empty')}
                </p>
              </If>

              <If condition={notifications.length > 0}>
                <ul>
                  {notifications.map((notification) => {
                    const bodyPreview =
                      formatCrmNotificationBodyPreview(notification);
                    const detailPath =
                      getCrmNotificationDetailPath(notification);
                    const isClickable = Boolean(detailPath);

                    const itemClassName = classNames(
                      'w-full border-b border-LIGHT_GRAY px-4 py-3 text-left last:border-b-0',
                      {
                        'bg-LIGHT_GRAY_5': !notification.isRead,
                        'bg-WHITE': notification.isRead,
                        'cursor-pointer hover:bg-LIGHT_GRAY_5': isClickable
                      }
                    );

                    const notificationContent = (
                      <>
                        <p className='text-sm text-DEFAULT_TEXT'>
                          {formatCrmNotificationMessage(notification)}
                        </p>
                        <If condition={Boolean(bodyPreview)}>
                          <p className='mt-1 text-xs text-DARK_GRAY line-clamp-2'>
                            {bodyPreview}
                          </p>
                        </If>
                        <If condition={Boolean(notification.createdAt)}>
                          <p className='mt-1 text-xs text-DARK_GRAY'>
                            {formatDate(notification.createdAt!)}
                          </p>
                        </If>
                      </>
                    );

                    return (
                      <li key={notification.id}>
                        {isClickable ? (
                          <button
                            type='button'
                            className={itemClassName}
                            onClick={() =>
                              handleNotificationClick(notification)
                            }
                          >
                            {notificationContent}
                          </button>
                        ) : (
                          <div className={itemClassName}>
                            {notificationContent}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </If>
            </div>
          </div>
        </If>
      </div>
    </OutsideClickHandler>
  );
};

export default SalesforceNotificationsBell;
